<template>
  <div class="grid grid-rows-[auto_1fr] overflow-hidden h-full">
    <div class="flex justify-between border-b-2">
      <nav
        role="section-nav"
        class="flex justify-between px-0 py-2 text-sm font-medium text-center text-gray-500">
        <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
          <li
            v-for="tab in section1Tabs"
            :key="tab.name"
            @click="selectedTab = tab.name"
            :class="variantClass(tab.name)">
            <p>{{ tab.label }}</p>
          </li>
        </ul>
      </nav>
      <div class="flex mr-2 space-x-1" v-if="selectedTab == 'orderList'">
        <BaseButton
          v-if="mode === 'view'"
          @click="onEditClicked"
          size="sm"
          outline
          color="light"
          class="gap-2 m-1 capitalize">
          <IconPencilSquare class="size-3" /> {{ $t('common.actions.edit') }}
        </BaseButton>
        <div v-if="mode === 'edit'" class="flex items-center justify-center gap-2">
          <BaseButton @click="onCancelClicked" size="xs" outline class="capitalize">
            {{ $t('common.actions.cancel') }}
          </BaseButton>
          <BaseButton @click="onSaveClicked" size="xs" class="capitalize">
            {{ $t('common.actions.save') }}
          </BaseButton>
        </div>
      </div>
    </div>
    <PriceCatalogsOrderList
      ref="orderListComponent"
      class="flex-1 overflow-auto"
      v-if="selectedTab == 'orderList'"
      :mode="mode"
      @add-all="mode = 'view'" />
    <PriceCatalogsDiscountMatrixTable v-else @matrix-details="goToMatrixDetails" />
  </div>
  <BaseDrawerConfirm
    :is-active="confirmModalActive"
    @cancel="confirmModalActive = false"
    @confirm="onDiscardChanges"></BaseDrawerConfirm>
</template>

<script setup lang="ts">
import { markRaw, ref, type Ref } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import IconPencilSquare from '@/assets/icons/IconPencilSquare.vue';
import PriceCatalogsOrderList from '@/components/prices/PriceCatalogsOrderList.vue';
import PriceCatalogsDiscountMatrixTable from '@/components/prices/PriceCatalogsDiscountMatrixTable.vue';
import { cva } from 'class-variance-authority';
import router from '@/router';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import { useI18n } from 'vue-i18n';

const variantClass = (tabName: string) => {
  return cva('py-2 w-48 text-start font-light ', {
    variants: {
      mode: {
        edit: 'pointer-events-none',
        view: 'cursor-pointer',
      },
    },
  })({
    mode: mode.value,
    class:
      tabName === selectedTab.value
        ? 'text-theme-500 border-theme-500 border-b-2'
        : 'text-gray-500',
  });
};

const { t: $t } = useI18n({ useScope: 'global' });
const section1Tabs = ref([
  {
    name: 'orderList',
    label: $t('pricing-catalogs.priceCatalogs'),
    component: markRaw(PriceCatalogsOrderList),
  },
  {
    name: 'matrix',
    label: $t('pricing-catalogs.discountMatrix'),
    component: markRaw(PriceCatalogsOrderList),
  },
]);
const selectedTab = ref('orderList');
const orderListComponent = ref<InstanceType<typeof PriceCatalogsOrderList> | null>(null);
const mode: Ref<'edit' | 'view'> = ref('view');

function onSaveClicked() {
  if (selectedTab.value == 'orderList' && orderListComponent.value)
    orderListComponent.value.onSave();
  mode.value = 'view';
}

function onEditClicked() {
  mode.value = 'edit';
}

function onCancelClicked() {
  if (orderListComponent.value && orderListComponent.value.isModified) {
    confirmModalActive.value = true;
    return;
  }
  mode.value = 'view';
}

function goToMatrixDetails(id: string) {
  router.push({ name: 'price-policies-matrix-details', params: { id: id } });
}

const confirmModalActive = ref(false);

function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (selectedTab.value == 'orderList' && orderListComponent.value)
    orderListComponent.value.onCancel();
}
</script>
